import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { X, Crown, Star, Sparkles, Check, Lock } from 'lucide-react';
import { Button } from './ui/button';

const PLANS = {
  stellar: {
    name: { en: 'Stellar', hi: 'स्टेलर' },
    price: 99,
    duration: { en: 'year', hi: 'साल' },
    profiles: 2,
    color: 'from-[#C84B32] to-[#E8913A]',
    icon: Star,
    features: {
      en: ['2 profiles', 'All features unlocked', 'Daily predictions', 'Detailed remedies'],
      hi: ['2 प्रोफाइल', 'सभी सुविधाएं अनलॉक', 'दैनिक भविष्यवाणी', 'विस्तृत उपाय']
    }
  },
  cosmic: {
    name: { en: 'Cosmic', hi: 'कॉस्मिक' },
    price: 199,
    duration: { en: 'year', hi: 'साल' },
    profiles: 'Unlimited',
    color: 'from-[#C84B32] to-[#F5B942]',
    icon: Crown,
    features: {
      en: ['Unlimited profiles', 'All features unlocked', 'Priority support', 'Family sharing'],
      hi: ['असीमित प्रोफाइल', 'सभी सुविधाएं अनलॉक', 'प्राथमिकता सहायता', 'परिवार साझाकरण']
    }
  }
};

const FEATURE_NAMES = {
  kundli: { en: 'Janam Kundli', hi: 'जन्म कुंडली' },
  chat: { en: 'AI Astrologer', hi: 'AI ज्योतिषी' },
  love_match: { en: 'Kundli Milan', hi: 'कुंडली मिलान' },
  palm_reading: { en: 'Palm Reading', hi: 'हस्तरेखा' },
  yearly_prediction: { en: 'Yearly Predictions', hi: 'वार्षिक भविष्यवाणी' },
  family_compatibility: { en: 'Family Compatibility', hi: 'पारिवारिक अनुकूलता' },
  profile: { en: 'Profile Creation', hi: 'प्रोफाइल बनाना' }
};

export const UpgradeModal = ({ 
  isOpen, 
  onClose, 
  feature = 'feature',
  recommendedPlan = 'stellar' 
}) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDark } = useTheme();

  const featureName = FEATURE_NAMES[feature]?.[language] || feature;
  const plan = PLANS[recommendedPlan] || PLANS.stellar;

  const handleUpgrade = (planId) => {
    onClose();
    navigate('/subscription');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className={`relative w-full max-w-md rounded-2xl p-6 border ${
            isDark 
              ? 'bg-white border-[#282622]/10' 
              : 'bg-white border-[#282622]/10 shadow-xl'
          }`}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className={`absolute top-4 right-4 p-1 rounded-full transition-colors ${
              isDark ? 'hover:bg-[#FCE9D9]' : 'hover:bg-[#FCE9D9]/60'
            }`}
          >
            <X className={`w-5 h-5 ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`} />
          </button>

          {/* Lock icon */}
          <div className="flex justify-center mb-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              isDark ? 'bg-amber-500/20' : 'bg-amber-100'
            }`}>
              <Lock className="w-8 h-8 text-amber-500" />
            </div>
          </div>

          {/* Title */}
          <h2 className={`text-xl font-semibold text-center mb-2 ${
            isDark ? 'text-white' : 'text-[#282622]'
          }`}>
            {language === 'hi' ? 'सुविधा अनलॉक करें' : 'Unlock Feature'}
          </h2>

          {/* Description */}
          <p className={`text-center mb-6 ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
            {language === 'hi' 
              ? `आपने ${featureName} का मुफ्त उपयोग कर लिया है। जारी रखने के लिए अपग्रेड करें।`
              : `You've used your free ${featureName}. Upgrade to continue using this feature.`
            }
          </p>

          {/* Plan cards */}
          <div className="space-y-3 mb-6">
            {Object.entries(PLANS).map(([id, p]) => (
              <motion.div
                key={id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleUpgrade(id)}
                className={`relative p-4 rounded-xl border cursor-pointer transition-all ${
                  id === recommendedPlan 
                    ? isDark 
                      ? 'border-[#C84B32] bg-[#C84B32]/10' 
                      : 'border-[#C84B32] bg-amber-50'
                    : isDark 
                      ? 'border-[#282622]/10 hover:border-[#282622]/15' 
                      : 'border-[#282622]/10 hover:border-[#282622]/10'
                }`}
              >
                {id === recommendedPlan && (
                  <span className={`absolute -top-2 right-4 px-2 py-0.5 text-[10px] font-medium rounded-full ${
                    isDark ? 'bg-[#C84B32] text-white' : 'bg-[#C84B32] text-white'
                  }`}>
                    {language === 'hi' ? 'अनुशंसित' : 'RECOMMENDED'}
                  </span>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center`}>
                      <p.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-[#282622]'}`}>
                        {p.name[language]}
                      </h3>
                      <p className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
                        {typeof p.profiles === 'number' ? p.profiles : (language === 'hi' ? 'असीमित' : 'Unlimited')} {language === 'hi' ? 'प्रोफाइल' : 'profiles'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-[#282622]'}`}>₹{p.price}</span>
                    <span className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>/{p.duration[language]}</span>
                  </div>
                </div>

                {/* Features */}
                <div className="mt-3 flex flex-wrap gap-1">
                  {p.features[language].slice(0, 2).map((f, i) => (
                    <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] ${
                      isDark ? 'bg-[#FCE9D9]/40 text-[#6B6560]' : 'bg-[#FCE9D9]/60 text-[#6B6560]'
                    }`}>
                      <Check className="w-3 h-3 text-green-500" />
                      {f}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* View all plans link */}
          <button
            onClick={() => {
              onClose();
              navigate('/subscription');
            }}
            className={`w-full text-center text-sm ${
              isDark ? 'text-[#C84B32] hover:text-[#C84B32]' : 'text-[#C84B32] hover:text-[#C84B32]'
            }`}
          >
            {language === 'hi' ? 'सभी योजनाएं देखें →' : 'View all plans →'}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Upgrade Banner component for showing on feature pages
export const UpgradeBanner = ({ 
  feature = 'feature',
  onUpgradeClick 
}) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDark } = useTheme();

  const featureName = FEATURE_NAMES[feature]?.[language] || feature;

  const handleClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      navigate('/subscription');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl p-4 border ${
        isDark 
          ? 'bg-gradient-to-r from-amber-500/10 to-[#C84B32]/10 border-amber-500/20' 
          : 'bg-gradient-to-r from-amber-50 to-[#FCE9D9] border-amber-200'
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isDark ? 'bg-amber-500/20' : 'bg-amber-100'
          }`}>
            <Lock className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-[#282622]'}`}>
              {language === 'hi' ? `${featureName} अनलॉक करें` : `Unlock ${featureName}`}
            </p>
            <p className={`text-xs ${isDark ? 'text-[#6B6560]' : 'text-[#6B6560]'}`}>
              {language === 'hi' 
                ? 'आपका मुफ्त उपयोग समाप्त हो गया'
                : 'Your free trial has been used'
              }
            </p>
          </div>
        </div>
        <Button
          onClick={handleClick}
          className="bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white text-sm px-4 py-2 rounded-lg"
        >
          <Sparkles className="w-4 h-4 mr-1" />
          {language === 'hi' ? 'अपग्रेड' : 'Upgrade'}
        </Button>
      </div>
    </motion.div>
  );
};

export default UpgradeModal;
